/**
 * Evolución de sistemas conocidos por año.
 * Basado en las imágenes de contextos/evolución_sistemas (MRG-A_AÑO_Nombre.png).
 * getSystemsForYear(year) devuelve sistemas conocidos hasta ese año, con posición en el plano (posiciones_mapa).
 */
import { SISTEMAS } from './definiciones_sistemas';
import { POSICIONES_MAPA } from './posiciones_mapa';

// Hitos de descubrimiento según cronología canónica (UEE, Vanduul, Xi'an, Banu, No reclamado).
const HITOS_POR_IMAGEN = [
    { year: 2271, systemIds: ['croshaw'] },
    { year: 2287, systemIds: ['rethor'] },
    { year: 2290, systemIds: ['null'] },
    { year: 2307, systemIds: ['fora'] },
    { year: 2317, systemIds: ['banshee'] },
    { year: 2341, systemIds: ['caliban'] },
    { year: 2356, systemIds: ['oberon'] },
    { year: 2362, systemIds: ['nemo'] },
    { year: 2365, systemIds: ['centauri'] },
    { year: 2402, systemIds: ['vega'] },
    { year: 2412, systemIds: ['virgil'] },
    { year: 2430, systemIds: ['davien', 'yulin'] },
    { year: 2438, systemIds: ['cathcart'] },
    { year: 2439, systemIds: ['bacchus'] },
    { year: 2441, systemIds: ['bremen'] },
    { year: 2445, systemIds: ['nexus'] },
    { year: 2449, systemIds: ['corel'] },
    { year: 2450, systemIds: ['geddon'] },
    { year: 2460, systemIds: ['ferron'] },
    { year: 2463, systemIds: ['kilian', 'cano'] },
    { year: 2467, systemIds: ['ellis'] },
    { year: 2469, systemIds: ['pyro'] },
    { year: 2473, systemIds: ['min', 'tanga'] },
    { year: 2474, systemIds: ['tiber'] },
    { year: 2478, systemIds: ['taranis'] },
    { year: 2481, systemIds: ['oretani'] },
    { year: 2493, systemIds: ['idris'] },
    { year: 2499, systemIds: ['magnus'] },
    { year: 2508, systemIds: ['terra'] },
    { year: 2510, systemIds: ['hadrian', 'kins'] },
    { year: 2514, systemIds: ['kiel'] },
    { year: 2515, systemIds: ['hades', 'kyukya', 'indra'] },
    { year: 2522, systemIds: ['baker'] },
    { year: 2524, systemIds: ['tayac'] },
    { year: 2528, systemIds: ['horus'] },
    { year: 2529, systemIds: ['yamon', 'helios'] },
    { year: 2530, systemIds: ['rilah'] },
    { year: 2531, systemIds: ['hadur'] },
    { year: 2532, systemIds: ['odin'] },
    { year: 2538, systemIds: ['charon'] },
    { year: 2539, systemIds: ['gurzil'] },
    { year: 2541, systemIds: ['elysium'] },
    { year: 2544, systemIds: ['castra'] },
    { year: 2563, systemIds: ['goss'] },
    { year: 2566, systemIds: ['oya'] },
    { year: 2582, systemIds: ['nyx'] },
    { year: 2583, systemIds: ['thusung', 'pallas'] },
    { year: 2602, systemIds: ['eelaut', 'ealuth', 'eealus'] },
    { year: 2617, systemIds: ['kayfa', 'kaipua'] },
    { year: 2650, systemIds: ['orion', 'ayrka', 'alika'] },
    { year: 2677, systemIds: ['leir'] },
    { year: 2712, systemIds: ['gliesse'] },
    { year: 2716, systemIds: ['tohil'] },
    { year: 2726, systemIds: ['vector', 'vermillion', 'virgo', 'vulture', 'vagabond', 'vesper', 'vendetta'] },
    { year: 2745, systemIds: ['trise'] },
    { year: 2759, systemIds: ['lauo', 'virtus'] },
    { year: 2784, systemIds: ['garron'] },
    { year: 2788, systemIds: ['osiris'] },
    { year: 2789, systemIds: ['tal'] },
    { year: 2811, systemIds: ['kellog'] },
    { year: 2812, systemIds: ['genesis'] },
    { year: 2823, systemIds: ['tyrol'] },
    { year: 2851, systemIds: ['stanton'] },
    { year: 2861, systemIds: ['oso'] },
    { year: 2863, systemIds: ['chronos'] },
    { year: 2877, systemIds: ['branaugh'] },
    { year: 2884, systemIds: ['viking'] },
    { year: 2921, systemIds: ['kallis'] },
    { year: 2941, systemIds: ['kabal'] },
    { year: 2943, systemIds: ['tamsa', 'vanguard'] },
    { year: 2945, systemIds: ['veritas', 'kaya', 'elsin', 'khabari', 'markahil'] }
];

/** Lista acumulada: para cada año, todos los systemIds conocidos hasta ese año (incl. Sol) */
function buildAcumulado() {
    const ordenados = [...HITOS_POR_IMAGEN].sort((a, b) => a.year - b.year);
    const acumulado = [];
    const yaIncluidos = new Set(['sol']);
    let listaActual = ['sol'];

    acumulado.push({
        fromYear: 0,
        systems: [{ ...SISTEMAS.sol, position: POSICIONES_MAPA.sol }]
    });

    for (const { year, systemIds } of ordenados) {
        for (const id of systemIds) {
            if (!yaIncluidos.has(id) && SISTEMAS[id]) {
                yaIncluidos.add(id);
                listaActual = [...listaActual, id];
            }
        }
        acumulado.push({
            fromYear: year,
            systems: listaActual.map((id) => ({
                ...SISTEMAS[id],
                position: POSICIONES_MAPA[id] || null
            }))
        });
    }
    return acumulado;
}

const MILESTONES = buildAcumulado();

/**
 * Devuelve los sistemas conocidos en el año dado (acumulado según imágenes).
 * @param {number} year - Año del timeline
 * @returns {Array} Lista de sistemas { id, name, sublabel?, type, bodies[] }
 */
export function getSystemsForYear(year) {
    const numYear = Number(year);
    if (!Number.isFinite(numYear) || MILESTONES.length === 0) {
        const last = MILESTONES[MILESTONES.length - 1]?.systems ?? [{ ...SISTEMAS.sol, position: POSICIONES_MAPA.sol }];
        return last;
    }
    let chosen = MILESTONES[0].systems;
    for (const { fromYear, systems } of MILESTONES) {
        if (fromYear <= numYear) chosen = systems;
        else break;
    }
    return chosen;
}

export { SISTEMAS };

/**
 * Lista de descubrimiento de sistemas (año + nombre) para la barra lateral.
 * @returns {Array<{ year: number, systemId: string, systemName: string }>}
 */
export function getSystemDiscoveries() {
    const list = [];
    for (const { year, systemIds } of HITOS_POR_IMAGEN) {
        for (const id of systemIds) {
            const sys = SISTEMAS[id];
            if (sys) list.push({
                year,
                systemId: id,
                systemName: sys.name.replace(/^Sistema\s+/i, ''),
                systemType: sys.type || 'unclaimed'
            });
        }
    }
    return list.sort((a, b) => a.year - b.year);
}
